import { debounce, formatMoney, theme } from './global.js';

const RECENT_KEY = 'energy:recent-searches';

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resultsEl = this.querySelector('[data-predictive-results]');
    this.form = this.querySelector('form');
    this.enabled = this.dataset.enabled !== 'false';
    if (!this.input || !this.enabled) return;

    this.onInput = debounce(this.search.bind(this), 220);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length > 1) this.search();
      else this.renderRecent();
    });
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.close();
    });
    this.form?.addEventListener('submit', () => this.rememberTerm(this.input.value));
  }

  onKeydown(e) {
    const options = Array.from(this.resultsEl.querySelectorAll('[role="option"]'));
    const activeIndex = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');

    if (e.key === 'Escape') {
      this.close();
      return;
    }
    if (!options.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = options[activeIndex + 1] || options[0];
      this.setActive(options, next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = options[activeIndex - 1] || options[options.length - 1];
      this.setActive(options, prev);
    } else if (e.key === 'Enter' && activeIndex > -1) {
      e.preventDefault();
      window.location.href = options[activeIndex].dataset.url;
    }
  }

  setActive(options, target) {
    options.forEach((o) => o.setAttribute('aria-selected', 'false'));
    target.setAttribute('aria-selected', 'true');
    target.scrollIntoView({ block: 'nearest' });
  }

  rememberTerm(term) {
    if (!term) return;
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((t) => t !== term);
      list.unshift(term);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6)));
    } catch (e) {
      /* localStorage unavailable */
    }
  }

  renderRecent() {
    let recent = [];
    try {
      recent = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (e) {
      recent = [];
    }
    if (!recent.length) return;
    this.resultsEl.innerHTML = `
      <div class="predictive-search__section">
        <div class="predictive-search__section-heading">
          <span>${theme.strings.recentSearches || 'Recent searches'}</span>
        </div>
        <ul>
          ${recent
            .map(
              (term) =>
                `<li role="option" data-url="${this.form.action}?q=${encodeURIComponent(term)}"><a href="${this.form.action}?q=${encodeURIComponent(term)}" class="predictive-search__recent-link">${term}</a></li>`
            )
            .join('')}
        </ul>
      </div>`;
    this.open();
  }

  async search() {
    const term = this.input.value.trim();
    if (term.length < 2) {
      this.requestId = (this.requestId || 0) + 1;
      this.classList.remove('is-loading');
      this.close();
      return;
    }
    // Show the spinner right away; ignore answers that arrive after a newer search was started.
    const requestId = (this.requestId = (this.requestId || 0) + 1);
    this.classList.add('is-loading');
    if (this.resultsEl.hidden || !this.resultsEl.children.length) {
      this.resultsEl.innerHTML = `<div class="predictive-search__loading" role="status"><span class="header-search__spinner" aria-hidden="true"></span><span>${theme.strings.searching || 'Searching…'}</span></div>`;
      this.open();
    }
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': 10,
      'resources[options][unavailable_products]': 'show',
    });
    try {
      const res = await fetch(`/search/suggest.json?${params.toString()}`);
      const data = await res.json();
      if (requestId !== this.requestId) return;
      this.renderResults(data.resources.results, term);
    } catch (e) {
      if (requestId === this.requestId) this.close();
    } finally {
      if (requestId === this.requestId) this.classList.remove('is-loading');
    }
  }

  renderResults(results, term) {
    const { products = [], collections = [], pages = [], articles = [] } = results;
    if (!products.length && !collections.length && !pages.length && !articles.length) {
      this.resultsEl.innerHTML = `<p class="predictive-search__empty">${theme.strings.noResults || 'No results found'}</p>`;
      this.open();
      return;
    }

    const toCents = (value) => Math.round(parseFloat(value) * 100);
    const money = (value) => formatMoney(toCents(value), theme.moneyFormat);
    const esc = (text) => String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    // In-stock products first, sold-out ones after them (stable, so relevance order is kept inside each group).
    const ranked = products
      .map((p, i) => ({ p, i }))
      .sort((a, b) => Number(Boolean(b.p.available)) - Number(Boolean(a.p.available)) || a.i - b.i)
      .map(({ p }) => p)
      .slice(0, 6);

    const productItems = ranked
      .map((p, i) => {
        const price = parseFloat(p.price);
        const compare = parseFloat(p.compare_at_price_max);
        const onSale = compare > price;
        const varies = p.price_min && p.price_max && parseFloat(p.price_min) !== parseFloat(p.price_max);
        const stock = p.available
          ? `<span class="predictive-search__stock is-in">${esc(theme.strings.inStock || 'In stock')}</span>`
          : `<span class="predictive-search__stock is-out">${esc(theme.strings.soldOut || 'Sold out')}</span>`;
        return `
        <li role="option" data-url="${esc(p.url)}" aria-selected="${i === 0 ? 'true' : 'false'}">
          <a href="${esc(p.url)}" class="predictive-search__product">
            <span class="predictive-search__thumb">${p.image ? `<img src="${esc(p.image)}" width="56" height="56" alt="" loading="lazy">` : ''}</span>
            <span class="predictive-search__info">
              <span class="predictive-search__product-title">${esc(p.title)}</span>
              <span class="predictive-search__meta">
                <span class="predictive-search__price">
                  ${varies && !onSale ? `<span class="predictive-search__from">${esc(theme.strings.from || 'From')}</span> ` : ''}
                  ${onSale ? `<span class="predictive-search__compare">${money(compare)}</span> ` : ''}<span class="${onSale ? 'predictive-search__sale' : ''}">${money(price)}</span>
                </span>
                ${stock}
              </span>
            </span>
          </a>
        </li>`;
      })
      .join('');

    const listSection = (title, items, urlKey = 'url', titleKey = 'title') =>
      items.length
        ? `<div class="predictive-search__section">
            <div class="predictive-search__section-heading"><span>${title}</span></div>
            <ul>${items
              .map((it) => `<li role="option" data-url="${it[urlKey]}"><a href="${it[urlKey]}">${it[titleKey]}</a></li>`)
              .join('')}</ul>
          </div>`
        : '';

    this.resultsEl.innerHTML = `
      ${productItems ? `<div class="predictive-search__section"><div class="predictive-search__section-heading"><span>${theme.strings.products || 'Products'}</span></div><ul>${productItems}</ul></div>` : ''}
      ${listSection(theme.strings.collections || 'Categories', collections.slice(0, 4))}
      ${listSection(theme.strings.pages || 'Pages', pages.slice(0, 3))}
      ${listSection(theme.strings.articles || 'Articles', articles.slice(0, 3))}
      <a class="predictive-search__view-all" href="${this.form.action}?q=${encodeURIComponent(term)}">${theme.strings.viewAllResults || 'View all results'}</a>
    `;
    this.open();
  }

  open() {
    this.resultsEl.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.resultsEl.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('predictive-search', PredictiveSearch);
